import cloneDeep from 'clone-deep';

import { BatchStatus } from '@interfaces/BatchStatus';
import GuestProps from '@interfaces/guest/GuestProps';
import { mapQuestionnairePackages } from '@service/QuestionnaireService';
import { ProfileWatermarkVO } from '@interfaces/profiles/ProfileWatermarkVO';
import ProfileContentVO, { ContentSourceStatus, ProfileContentType } from '@interfaces/contents/ProfileContentVO';
import { generateWatermark, getBatchStatusForContents, getContentByIds, getDownloadBatchDetails, getWatermarkBatchDetails, handleProfileDownload, reuploadContent } from '@service/ProfileService';

import { FileDownloadInput, getFileDownloadURL } from './FileUtils';
import { getAnonymousViewerId } from '../profile/ProfileAccessManagementUtils';

/** Response shape from `mapQuestionnairePackages` batch status polling */
type MapQuestionnairePackagesResult = {
  [BatchStatus.COMPLETED]?: Record<string, unknown>;
  [BatchStatus.ERROR]?: unknown[];
};

interface WatermarkBatchDetailsVO {
  status: BatchStatus;
  data?: {
    watermarkedFileId?: string;
    watermarkDownloadauthkey?: string;
  };
}

interface DownloadBatchPollVO {
  status: BatchStatus | string;
}

const pqBatchInitializer = (contentVO: ProfileContentVO, onCompleted: (updatedContentVO: ProfileContentVO) => void, onFailed: () => void, guestProps?: GuestProps) => {
  const { isPublicUser, companyId } = guestProps || {};
  const interval = setInterval(() => {
    mapQuestionnairePackages(
      [contentVO.sourceRefId],
      {
        [contentVO.sourceRefId]: {
          profileId: contentVO.profileId,
          sectionId: contentVO.sectionId,
          contentId: contentVO.id
        }
      },
      Boolean(isPublicUser),
      companyId || '',
      (result: MapQuestionnairePackagesResult) => {
        if (result[BatchStatus.COMPLETED] || result[BatchStatus.ERROR]) {
          const updatedContentVO = { ...contentVO };
          if (!updatedContentVO.contentInfo) {
            updatedContentVO.contentInfo = {};
          }
          if (result[BatchStatus.COMPLETED] && result[BatchStatus.COMPLETED][contentVO.sourceRefId]) {
            updatedContentVO.contentInfo.packageRequests = result[BatchStatus.COMPLETED][contentVO.sourceRefId];
            onCompleted(updatedContentVO);
          } else if (result[BatchStatus.ERROR] && result[BatchStatus.ERROR].length > 0) {
            if (updatedContentVO.contentInfo?.packageRequests) {
              updatedContentVO.contentInfo.packageRequests = [{ status: BatchStatus.ERROR }];
            } else {
              updatedContentVO.contentInfo.packageRequests.status = BatchStatus.ERROR;
            }
          } else {
            // do nothing
          }
          clearInterval(interval);
        }
      },
      () => {
        onFailed();
        clearInterval(interval);
      }
    );
  }, 5 * 1000);
};

const pdBatchInitializer = (contentVO: ProfileContentVO, onCompleted: (updatedContentVO: ProfileContentVO) => void, onFailed: () => void, guestProps?: GuestProps) => {
  const { isPublicUser, companyId } = guestProps || {};
  const interval: NodeJS.Timeout = setInterval(() => {
    getBatchStatusForContents(
      contentVO.profileId,
      contentVO.sectionId,
      [contentVO.id],
      (result: { [key: string]: string[] }) => {
        if (result[ContentSourceStatus.READY] || result[ContentSourceStatus.ERROR]) {
          if (result[ContentSourceStatus.READY] && result[ContentSourceStatus.READY].includes(contentVO.id)) {
            getContentByIds(
              contentVO.profileId,
              contentVO.sectionId,
              [contentVO.id],
              (contents: ProfileContentVO[]) => {
                if (contents?.length > 0) {
                  onCompleted(contents[0]);
                }
                clearInterval(interval);
              },
              () => {
                onFailed();
                clearInterval(interval);
              },
              isPublicUser,
              companyId
            );
          } else {
            onFailed();
            clearInterval(interval);
          }
        }
      },
      () => {
        onFailed();
        clearInterval(interval);
      },
      isPublicUser,
      companyId
    );
  }, 5 * 1000);
};

export const waterMarkBatchInitializer = (
  batchId: string,
  entityType: ProfileContentType | undefined,
  contentVO: ProfileContentVO,
  onCompleted: (updatedContentVO: ProfileContentVO) => void,
  onFailed: () => void,
  guestProps?: Partial<GuestProps>
) => {
  const { isPublicUser, companyId } = guestProps || {};
  const interval: NodeJS.Timeout = setInterval(() => {
    getWatermarkBatchDetails(
      batchId,
      (batchVO: WatermarkBatchDetailsVO) => {
        if (batchVO.status === BatchStatus.COMPLETED || batchVO.status === BatchStatus.ERROR) {
          if (batchVO.status === BatchStatus.COMPLETED && batchVO.data?.watermarkedFileId) {
            if (entityType === ProfileContentType.PQ) {
              contentVO.contentInfo.packageRequests[0].watermarkedFileId = batchVO.data.watermarkedFileId;
              contentVO.contentInfo.packageRequests[0].watermarkDownloadauthkey = batchVO.data.watermarkDownloadauthkey;
              contentVO.contentInfo.packageRequests[0].packageName = contentVO.name;
            } else {
              contentVO.contentInfo.watermarkedFileId = batchVO.data.watermarkedFileId;
              contentVO.contentInfo.watermarkDownloadauthkey = batchVO.data.watermarkDownloadauthkey;
            }
            onCompleted(contentVO);
            clearInterval(interval);
          } else {
            onFailed();
            clearInterval(interval);
          }
        }
      },
      () => {
        onFailed();
        clearInterval(interval);
      },
      isPublicUser,
      companyId
    );
  }, 5 * 1000);
};

export const handleDownload = (
  contentType: string,
  contentVO: ProfileContentVO,
  loadingCallback: (loading: boolean) => void,
  onFailed: (error: string) => void,
  guestProps: GuestProps,
  downloadMaskedFile?: boolean,
  watermarkVO?: ProfileWatermarkVO,
  fromInterval?: boolean,
  onViewCallBack?: (documentSrc: Record<string, unknown>) => void
) => {
  const { isPublicUser, guestKey, isAccessVerified, companyId } = guestProps;
  let entityType: ProfileContentType | undefined;
  let error: boolean = contentVO.sourceStatus === ContentSourceStatus.ERROR;

  let downloadUrl: string = '';
  let documentSrcVO: Record<string, unknown> = {};
  switch (contentType) {
    case ProfileContentType.DOCUMENT:
    case ProfileContentType.PD:
      if (
        contentVO.sourceStatus !== ContentSourceStatus.ERROR &&
        contentVO.contentInfo?.fileId &&
        (!downloadMaskedFile ||
          (watermarkVO?.includeTimeStamp && fromInterval && contentVO.contentInfo.watermarkedFileId) ||
          (!watermarkVO?.includeTimeStamp && contentVO.contentInfo.watermarkedFileId && (fromInterval || contentVO.contentInfo.watermarkedFileVersion === watermarkVO?.version)))
      ) {
        const fileVO = cloneDeep(contentVO.contentInfo);

        // If watermark enabled and watermark latest version file already generated then generate downloadUrl with masked file
        if (downloadMaskedFile) {
          fileVO.fileId = contentVO.contentInfo.watermarkedFileId;
          fileVO.downloadAuthKey = contentVO.contentInfo.watermarkDownloadauthkey;
          fileVO.name = `${(contentVO.name || contentVO.contentInfo.name).substring(0, (contentVO.name || contentVO.contentInfo.name).lastIndexOf('.'))}.pdf`;
        }
        documentSrcVO = fileVO as Record<string, unknown>;
        downloadUrl = getFileDownloadURL(fileVO as FileDownloadInput, {
          urlPrefix: isAccessVerified ? 'profiles' : `public/${companyId}/profiles`,
          guestKey
        });
      }
      entityType = ProfileContentType.PD;
      break;
    case ProfileContentType.PROJECT:
    case ProfileContentType.PQ:
      if (contentVO.contentInfo?.packageRequests?.length > 0) {
        const packageVO = cloneDeep(contentVO.contentInfo.packageRequests[0]);
        packageVO.packageName = contentVO.name;
        if (
          packageVO.fileId &&
          (!downloadMaskedFile ||
            (watermarkVO?.includeTimeStamp && fromInterval && packageVO.watermarkedFileId) ||
            (!watermarkVO?.includeTimeStamp && packageVO.watermarkedFileId && (fromInterval || packageVO.watermarkedFileVersion === watermarkVO?.version)))
        ) {
          // If watermark enabled and watermark latest version file already generated then generate downloadUrl with masked file
          if (downloadMaskedFile) {
            packageVO.fileId = packageVO.watermarkedFileId;
            packageVO.downloadAuthKey = packageVO.watermarkDownloadauthkey;
          }
          downloadUrl = getFileDownloadURL(
            { ...packageVO, fileName: `${contentVO.name || contentVO.contentInfo.name}.zip` },
            {
              urlPrefix: isAccessVerified ? 'profiles' : `public/${companyId}/profiles`,
              guestKey
            }
          );
        } else if (packageVO.status === BatchStatus.ERROR) {
          error = true;
        } else {
          // do nothing
        }
      }
      entityType = ProfileContentType.PQ;
      break;
    case ProfileContentType.PQ_ATTACHMENT:
      if (
        contentVO.contentInfo?.fileId &&
        (!downloadMaskedFile ||
          (watermarkVO?.includeTimeStamp && fromInterval && contentVO.contentInfo.watermarkedFileId) ||
          (!watermarkVO?.includeTimeStamp && contentVO.contentInfo.watermarkedFileId && (fromInterval || contentVO.contentInfo.watermarkedFileVersion === watermarkVO?.version)))
      ) {
        // If watermark enabled and watermark latest version file already generated then generate downloadUrl with masked file
        const attachmentVO = cloneDeep(contentVO.contentInfo);
        if (downloadMaskedFile) {
          attachmentVO.fileId = contentVO.contentInfo.watermarkedFileId;
          attachmentVO.downloadAuthKey = contentVO.contentInfo.watermarkDownloadauthkey;
          if (contentVO.contentInfo.name) {
            attachmentVO.name = `${(contentVO.name || contentVO.contentInfo.name).substring(0, (contentVO.name || contentVO.contentInfo.name).lastIndexOf('.'))}.pdf`;
          } else {
            attachmentVO.fileName = `${(contentVO.name || contentVO.contentInfo.name).substring(0, (contentVO.name || contentVO.contentInfo.name).lastIndexOf('.'))}.pdf`;
          }
        }
        documentSrcVO = attachmentVO as Record<string, unknown>;
        downloadUrl = getFileDownloadURL(attachmentVO as FileDownloadInput, {
          urlPrefix: isAccessVerified ? 'profiles' : `public/${companyId}/profiles`,
          guestKey
        });
      }
      entityType = ProfileContentType.PQ_ATTACHMENT;
      break;
    default:
    // do nothing
  }

  if (downloadUrl) {
    if (onViewCallBack) {
      onViewCallBack(documentSrcVO);
    } else {
      const isAnonymousViewer: boolean = Boolean(isPublicUser && !isAccessVerified);
      let anonymousViewId = null;
      if (isAnonymousViewer) {
        anonymousViewId = getAnonymousViewerId();
      }
      handleProfileDownload(downloadUrl, anonymousViewId, onFailed);
    }
    loadingCallback(false);
  } else {
    if (entityType === ProfileContentType.PD && contentVO.sourceStatus === ContentSourceStatus.ERROR) {
      loadingCallback(true);
      if (!contentVO.retryCount || contentVO.retryCount <= 0) {
        reuploadContent(
          contentVO.profileId,
          contentVO.sectionId,
          contentVO.id,
          (updatedContent: ProfileContentVO) => {
            if (updatedContent.sourceStatus === ContentSourceStatus.PENDING) {
              pdBatchInitializer(
                contentVO,
                updatedContentVO => handleDownload(contentType, updatedContentVO, loadingCallback, onFailed, guestProps, downloadMaskedFile, watermarkVO, false, onViewCallBack),
                () => loadingCallback(false),
                guestProps
              );
            } else {
              handleDownload(contentType, updatedContent, loadingCallback, onFailed, guestProps, downloadMaskedFile, watermarkVO, false, onViewCallBack);
            }
          },
          () => {
            loadingCallback(false);
          },
          guestProps
        );
      } else {
        loadingCallback(false);
      }
    }

    if (!error) {
      loadingCallback(true);
      if (entityType === ProfileContentType.PQ && (!contentVO.contentInfo?.packageRequests || contentVO.contentInfo.packageRequests.length === 0 || !contentVO.contentInfo.packageRequests[0].fileId)) {
        pqBatchInitializer(
          contentVO,
          updatedContentVO => handleDownload(contentType, updatedContentVO, loadingCallback, onFailed, guestProps, downloadMaskedFile, watermarkVO, false, onViewCallBack),
          () => loadingCallback(false),
          guestProps
        );
      } else if (entityType === ProfileContentType.PD && !contentVO.contentInfo.fileId) {
        pdBatchInitializer(
          contentVO,
          updatedContentVO => handleDownload(contentType, updatedContentVO, loadingCallback, onFailed, guestProps, downloadMaskedFile, watermarkVO, false, onViewCallBack),
          () => loadingCallback(false),
          guestProps
        );
      } else if (downloadMaskedFile) {
        const postBody = {
          profileId: contentVO.profileId,
          sectionId: contentVO.sectionId,
          contentId: contentVO.id,
          entityId: entityType === ProfileContentType.PQ_ATTACHMENT ? contentVO.contentInfo.fileId : contentVO.contentInfo.id,
          entityType
        };
        generateWatermark(
          postBody,
          guestProps,
          (result: { [key: string]: string }) => {
            if (result.batchId) {
              waterMarkBatchInitializer(
                result.batchId,
                entityType,
                contentVO,
                updatedContentVO => handleDownload(contentType, updatedContentVO, loadingCallback, onFailed, guestProps, downloadMaskedFile, watermarkVO, true, onViewCallBack),
                () => handleDownload(contentType, contentVO, loadingCallback, onFailed, guestProps, false, watermarkVO, true, onViewCallBack),
                guestProps
              );
            } else {
              loadingCallback(false);
            }
          },
          () => loadingCallback(false)
        );
      } else {
        loadingCallback(false);
      }
    }
  }
};

export const downloadBatchInitiator = (companyId: string, profileId: string, batchId: string, isPublicUser: boolean, callBack: (response: unknown) => void) => {
  const interval = setInterval(() => {
    getDownloadBatchDetails(
      companyId,
      profileId,
      batchId,
      isPublicUser,
      (batchVO: DownloadBatchPollVO) => {
        if (batchVO.status === BatchStatus.COMPLETED || batchVO.status === BatchStatus.ERROR) {
          if (batchVO.status === BatchStatus.COMPLETED) {
            clearInterval(interval);
            callBack(batchVO);
          } else {
            clearInterval(interval);
          }
        }
      },
      () => {
        clearInterval(interval);
      }
    );
  }, 2 * 1000);
};
